/**
 * Which module outcomes provide the evidence for each programme outcome.
 *
 * @module components/studio/panels/MappingPanel
 */

import React from "react";

import { MimloReference } from "@/components/studio/MimloReference";
import { Checkbox, MutedText, Panel, Table } from "@/components/ui";
import type { MIMLO, PLO } from "@/types";

export function MappingPanel({
  mimlos,
  plos,
  ploToMimlos,
  onToggle,
}: {
  mimlos: readonly MIMLO[];
  plos: readonly PLO[];
  ploToMimlos: Record<string, string[]>;
  onToggle: (ploId: string, mimloId: string, checked: boolean) => void;
}): React.ReactElement {
  return (
    <Panel
      aria-label="Programme outcome mapping"
      description="Programme outcomes are read only. Select the module outcomes that provide evidence for each one."
    >
      <MimloReference
        mimlos={mimlos}
        scope="this mapping"
        data-testid="module-mapping-mimlo-reference"
      />
      <Table
        caption="Programme learning outcomes against this module's outcomes"
        className="mt-5"
        data-testid="module-workspace-mapping"
      >
        <thead>
          <tr>
            <th scope="col">Programme learning outcome</th>
            {mimlos.map((mimlo, index) => (
              <th
                key={mimlo.id}
                scope="col"
                className="text-center"
                data-testid={`module-mapping-mimlo-header-${index + 1}`}
              >
                Outcome {index + 1}
                <span className="sr-only">
                  : {mimlo.text || "No outcome wording has been added yet."}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {plos.length ? (
            plos.map((plo, ploIndex) => (
              <tr key={plo.id}>
                <th scope="row" className="min-w-72 text-start font-medium">
                  {plo.code || `Outcome ${ploIndex + 1}`}
                  <MutedText>{plo.text}</MutedText>
                </th>
                {mimlos.map((mimlo, mimloIndex) => (
                  <td key={mimlo.id} className="text-center">
                    <Checkbox
                      aria-label={`Map ${plo.code || `outcome ${ploIndex + 1}`} to module outcome ${mimloIndex + 1}`}
                      checked={(ploToMimlos[plo.id] ?? []).includes(mimlo.id)}
                      onChange={(checked) => onToggle(plo.id, mimlo.id, checked)}
                    />
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={Math.max(1, mimlos.length + 1)} className="py-8 text-center text-muted">
                Programme learning outcomes have not been defined yet.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </Panel>
  );
}

export default MappingPanel;
